import { FunctionComponent, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams, useLocation } from 'react-router-dom';
import { Flex, Box } from '@rebass/emotion';

import { getIntlValidLocale } from '@app/helpers/locale';
import ErrorBoundary from '@app/components/ErrorBoundary';

import { PricingPage_content_pricing_pages_layout_item_pricing_pages_single_layout_product as DirectusProduct } from '../../__generated__/PricingPage';
import ProductVariant from '../ProductVariant';
import ProductVariantInfo from '../ProductVariantInfo';
import QuantityDropdown from '../QuantityDropdown';
import ProductUpgradeMessage from '../ProductUpgradeMessage';
import AddToCartButton from '../AddToCartButton';
import CartErrors from '../CartErrors';

import messages from './ProductLayout.messages';
import { LayoutTitle, LayoutSubtitle, FindDealerButton } from './styles';
import useShopProduct from '../../hook/useShopProduct';
import {
  PricingPageShopProduct_magento2_products_items_Magento2_ConfigurableProduct as ConfigurableMagentoProduct,
  PricingPageShopProduct_magento2_products_items_Magento2_ConfigurableProduct_variants as Variant,
} from '../../hook/__generated__/PricingPageShopProduct';
import useLocaleWithFallback from '../../../../hooks/useLocaleWithFallback';
import useProductVariant from '../../hook/useProductVariant';

const ProductLayout: FunctionComponent<{
  product: DirectusProduct;
  magentoProduct: ConfigurableMagentoProduct;
  showRetailerButton: boolean | null;
  onSelectVariant: (variant: Variant | null) => Variant | null | void;
}> = ({
  product,
  magentoProduct,
  showRetailerButton,
  onSelectVariant,
}): JSX.Element => {
  const { language } = useParams<{
    language: string;
  }>();
  const searchQuery = new URLSearchParams(useLocation().search);
  const preselectedSKU = searchQuery.get('variant');

  const { findTranslationByKeyWithFallback } = useLocaleWithFallback();

  const { title } = product;
  const subtitle = findTranslationByKeyWithFallback({
    translations: product?.translations,
    key: 'subtitle',
  });

  const [quantity, setQuantity] = useState(1);
  const { isAuthenticated, country } = useShopProduct(product);
  const { variants, configurableOptions } = magentoProduct;
  const { selectedVariant, setSelectedVariant } = useProductVariant(
    variants,
    preselectedSKU
  );

  useEffect(() => {
    onSelectVariant(selectedVariant);
  }, [selectedVariant, onSelectVariant]);

  return (
    <ErrorBoundary>
      <Box mb={5}>
        <CartErrors />
        <Box width={'100%'} mb={5}>
          <LayoutTitle data-testid="pricing-product-layout-title">
            {title}
          </LayoutTitle>
          <LayoutSubtitle>{subtitle}</LayoutSubtitle>
        </Box>
        <Flex justifyContent="space-between" flexWrap="wrap">
          <Box width={['100%', 1 / 3]} mb={[5, 0]}>
            <img
              data-testid="pricing-product-layout-image"
              src={selectedVariant?.product?.image?.url ?? undefined}
              alt={selectedVariant?.product?.image?.label ?? undefined}
            />
          </Box>
          <Flex
            width={['100%', 2 / 3]}
            mb={[5, 0]}
            justifyContent="space-between"
            flexWrap="wrap"
          >
            <Box
              width={['100%', '100%', 1 / 2]}
              mb={5}
              px={[0, 30, 10]}
              as="form"
            >
              <ProductVariant
                variants={variants || []}
                selectedVariant={selectedVariant}
                configurableOptions={configurableOptions || []}
                onChange={setSelectedVariant}
              />
              <Box mt={20}>
                <ProductUpgradeMessage
                  isAuthenticated={isAuthenticated}
                  selectedVariant={selectedVariant}
                  selectVariant={setSelectedVariant}
                  variants={variants}
                />
              </Box>
              <Box mt={30}>
                <QuantityDropdown onChange={setQuantity} value={quantity} />
              </Box>
            </Box>
            <Box width={['100%', '100%', 1 / 2]} mb={[5, 0]} px={[0, 30, 50]}>
              {selectedVariant && (
                <ProductVariantInfo
                  variant={selectedVariant}
                  countryCode={country}
                />
              )}
              <Flex justifyContent="flex-start">
                <Flex flexDirection="column" flexGrow={['1', 'unset']}>
                  <AddToCartButton
                    product={selectedVariant?.product}
                    quantity={quantity}
                  >
                    <FormattedMessage {...messages.addToCart} />
                  </AddToCartButton>

                  {showRetailerButton && (
                    <FindDealerButton
                      href={`/${getIntlValidLocale(
                        language
                      )}/company/resellers`}
                    >
                      <FormattedMessage {...messages.findDealer} />
                    </FindDealerButton>
                  )}
                </Flex>
              </Flex>
            </Box>
          </Flex>
        </Flex>
      </Box>
    </ErrorBoundary>
  );
};

export default ProductLayout;
